import { Toast } from '@gafe/common-ui';
import axios, { AxiosResponse } from 'axios';
import { put, select, takeEvery } from 'redux-saga/effects';

import {
  ActivityInstance,
  InfoToFetchActivityData,
  refetchActivities,
} from '@/controllers/Activity';
import { updateDetailsSidePage } from '@/controllers/Opportunity';
import { GoogleAnalytics } from '@/services/Analytics';
import { selectActivityDetailsData } from '@/store/reducers/activities/selectors';
import { setGlobalError } from '@/store/reducers/global/actions';
import { OrderMetricsActionTypes, OrdersActionTypes } from '@/store/reducers/orders/action-types';
import { CustomExceptions, ReduxAction } from '@/types';

import { OrderController } from './OrderController';

function* createDesignToOrderHandler(action: ReduxAction) {
  try {
    const result: AxiosResponse<{ id: string }> = yield OrderController.createDesignToOrder(
      action.payload,
    );

    GoogleAnalytics.createOrder(action.payload.opportunityId, action.proposalId, false);
    yield put({ type: OrdersActionTypes.CREATE_DESIGN_TO_ORDER.SUCCESS, payload: result.data.id });

    const { activityId } = yield select(selectActivityDetailsData);
    const activityData: InfoToFetchActivityData = {
      activityType: ActivityInstance.OrderWithoutProject,
      activityId,
    };
    // Re-fetch data, that is visible on the page, so it will be fresh and "up to date"
    const activities = yield refetchActivities();

    // Activity data is passed if we need to update details side page
    if (activityData) yield updateDetailsSidePage(activityData, activities);
  } catch (error: GlobalAny) {
    yield put({ type: OrdersActionTypes.CREATE_DESIGN_TO_ORDER.FAIL, error });

    console.error(error);
    yield put(setGlobalError(new Error(error?.response?.data.Message)));
  }
}

function* setTaskDates(action) {
  const { sagaPayload } = action;
  const { deliveryDate, installDate, orderId, callback } = sagaPayload;

  try {
    if (deliveryDate?.value) {
      yield OrderController.setTaskDate(orderId, 'DELIVERY', deliveryDate?.value);
      yield put({
        type: OrdersActionTypes.SET_TASK_DATE.SUCCESS,
        sagaPayload: { dateTaskType: 'DELIVERY', value: deliveryDate?.value, hasError: false },
      });
    }

    if (installDate?.value) {
      yield OrderController.setTaskDate(orderId, 'INSTALL', installDate?.value);
      yield put({
        type: OrdersActionTypes.SET_TASK_DATE.SUCCESS,
        sagaPayload: { dateTaskType: 'INSTALL', value: installDate?.value, hasError: false },
      });
    }

    callback();
  } catch (error: GlobalAny) {
    console.error(error);
    Toast({
      title: 'Something went wrong',
      message: error?.response?.data.message,
      type: 'destructive',
    });
    yield put({
      type: OrdersActionTypes.SET_TASK_DATE.FAIL,
      sagaPayload: { dateTaskType: 'DELIVERY', value: '', hasError: true },
    });
    yield put({
      type: OrdersActionTypes.SET_TASK_DATE.FAIL,
      sagaPayload: { dateTaskType: 'INSTALL', value: '', hasError: true },
    });
  }
}

function* scheduleDateCleanUp() {
  yield put({
    type: OrdersActionTypes.SCHEDULE_DATE_CLEAN_UP.SUCCESS,
  });
}

export function* fetchOrderMetric(action: ReduxAction) {
  const { dateOption } = action.sagaPayload;

  try {
    const { data } = yield OrderController.fetchOrderMetrics(dateOption);

    yield put({
      type: OrderMetricsActionTypes.FETCH_ORDER_METRIC.SUCCESS,
      payload: data,
    });
  } catch (error) {
    yield put({ type: OrderMetricsActionTypes.FETCH_ORDER_METRIC.FAIL, error });

    if (axios.isAxiosError(error)) {
      if (error?.response?.status === 400) {
        yield put(setGlobalError(new Error(CustomExceptions.PageNotFound)));
      } else {
        console.error(error);
      }
    }
  }
}

export function* CreateNewOrderSaga() {
  yield takeEvery(OrdersActionTypes.CREATE_DESIGN_TO_ORDER.START, createDesignToOrderHandler);
  yield takeEvery(OrdersActionTypes.SET_TASK_DATE.START, setTaskDates);
  yield takeEvery(OrdersActionTypes.SCHEDULE_DATE_CLEAN_UP.START, scheduleDateCleanUp);
  yield takeEvery(OrderMetricsActionTypes.FETCH_ORDER_METRIC.START, fetchOrderMetric);
}
